// In-app feedback form → Firestore `feedback` collection.
import { useState } from "react";
import { addDoc, collection } from "firebase/firestore";
import { db, LS } from "../lib/firebase";
import { themeTokens } from "../lib/theme";
import { Btn, Icon, useToast, useFormGuard, APP_VERSION } from "../ui/primitives";

export const FeedbackForm=({theme,onClose,userId,profile})=>{
  useFormGuard(); // pause background sync while this form is open
  const toast=useToast();
  const [rating,setRating]=useState(0);
  const [category,setCategory]=useState("General");
  const [msg,setMsg]=useState("");
  const [submitted,setSubmitted]=useState(false);
  const [submitting,setSubmitting]=useState(false);
  const [msgErr,setMsgErr]=useState("");
  const {isDark,tc,mc}=themeTokens(theme);
  const p=theme?.primary||"#0f766e";

  const submit=async()=>{
    if(!rating){toast("Please select a star rating.","warning");return;}
    if(!msg.trim()){setMsgErr("Please write your feedback.");return;}
    setMsgErr("");
    setSubmitting(true);
    const entry={
      userId:userId||"anonymous",
      name:profile?.name||"",
      businessName:profile?.businessName||"",
      rating,
      category,
      message:msg,
      version:APP_VERSION,
      submittedAt:new Date().toISOString(),
    };
    try{
      // Save to Firestore /feedback collection
      await addDoc(collection(db,"feedback"),entry);
    }catch(e){
      // Fallback to localStorage if Firestore fails
      const existing=LS.get("ufcm_feedback")||[];
      LS.set("ufcm_feedback",[entry,...existing]);
    }
    setSubmitting(false);
    setSubmitted(true);
    setTimeout(()=>onClose(),2500);
  };

  if(submitted)return(
    <div className="text-center py-8 space-y-3">
      <div className="text-5xl mb-2">🙏</div>
      <p className="font-bold text-lg" style={{color:tc}}>Thank you for your feedback!</p>
      <p className="text-sm" style={{color:mc}}>We'll review it and use it to make Prabzo better.</p>
      <p className="text-xs" style={{color:mc,opacity:0.6}}>This window will close shortly...</p>
    </div>
  );

  return(
    <div className="space-y-5">
      {/* Star Rating */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{color:mc}}>Overall Experience</p>
        <div className="flex gap-2">
          {[1,2,3,4,5].map(s=>(
            <button key={s} onClick={()=>setRating(s)}
              className="text-2xl transition-transform hover:scale-110"
              style={{filter:s<=rating?"none":"grayscale(1) opacity(0.4)"}}>
              ⭐
            </button>
          ))}
          {rating>0&&<span className="text-xs self-center ml-1 font-semibold" style={{color:p}}>
            {["","Needs work","Could be better","Good","Great","Love it!"][rating]}
          </span>}
        </div>
      </div>

      {/* Category */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{color:mc}}>Category</p>
        <div className="flex flex-wrap gap-2">
          {["General","UI / Design","Clients","Appointments","Payments","Attendance","Bug Report","Feature Request"].map(cat=>(
            <button key={cat} onClick={()=>setCategory(cat)}
              className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-all"
              style={{background:category===cat?p:"transparent",color:category===cat?"#fff":(isDark?"#94a3b8":"#6b7280"),borderColor:category===cat?p:(isDark?"#334155":"#e5e7eb")}}>
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Message */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold uppercase tracking-wider" style={{color:mc}}>Your Message *</label>
        <textarea className="border rounded-xl px-4 py-3 text-sm focus:outline-none resize-none"
          style={{background:isDark?"#0f172a":"#f9fafb",borderColor:msgErr?"#ef4444":(isDark?"#334155":"#e5e7eb"),color:tc,minHeight:"100px"}}
          placeholder="Tell us what you think — what's great, what's confusing, or what you wish existed..."
          value={msg} onChange={e=>{setMsg(e.target.value);setMsgErr("");}}/>
        {msgErr&&<p className="text-red-500 text-xs">{msgErr}</p>}
      </div>

      <div className="flex justify-end gap-3">
        <Btn variant="secondary" onClick={onClose} theme={theme}>Cancel</Btn>
        <Btn onClick={submit} theme={theme} disabled={submitting}>
          <Icon name="check" size={16}/>{submitting?"Submitting...":"Submit Feedback"}
        </Btn>
      </div>
    </div>
  );
};
